import { useState } from 'react'
import { apiError, errStatus } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, ChevronLeft, ChevronRight, Clock, User,
  Leaf, Droplets, Scissors, Sprout, FlaskConical,
  Thermometer, Eye, Wrench, Wheat, AlignLeft,
  X, CheckCircle2, AlertTriangle, Calendar,
} from 'lucide-react'
import { api, activityApi, yieldImpactApi, type ParsedEvent } from '../lib/api'
import { NarrativeEventCapture } from '../components/shared/NarrativeEventCapture'

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      '#121810',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#2D6A2F',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
  red:       '#F87171',
  blue:      '#60A5FA',
  teal:      '#2DD4BF',
}

// ── Action type config ────────────────────────────────────────────────────────
const ACTION_TYPES = [
  { value: 'fertilize',   label: 'Fertilize',    icon: FlaskConical, color: '#A78BFA' },
  { value: 'prune',       label: 'Prune',         icon: Scissors,    color: '#F472B6' },
  { value: 'spray',       label: 'Spray',         icon: Droplets,    color: '#38BDF8' },
  { value: 'irrigate',    label: 'Irrigate',      icon: Droplets,    color: '#34D399' },
  { value: 'harvest',     label: 'Harvest',       icon: Wheat,       color: C.amber  },
  { value: 'graft',       label: 'Graft',         icon: Sprout,      color: '#86EFAC' },
  { value: 'scion_push',  label: 'Scion Push',    icon: Leaf,        color: '#4ADE80' },
  { value: 'soil_test',   label: 'Soil Test',     icon: Thermometer, color: '#FCA5A5' },
  { value: 'train',       label: 'Train',         icon: Wrench,      color: '#FDBA74' },
  { value: 'observation', label: 'Observation',   icon: Eye,         color: C.textMuted },
  { value: 'custom',      label: 'Custom',        icon: AlignLeft,   color: C.textFaint },
]

function actionConfig(type: string) {
  return ACTION_TYPES.find(a => a.value === type) ?? ACTION_TYPES[ACTION_TYPES.length - 1]
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function toDateStr(d: Date) {
  return d.toISOString().slice(0, 10)
}

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface ActivityEvent {
  id: string
  actor_label: string
  actor_type: string
  action: string
  description: string
  resource_type?: string
  resource_label?: string
  occurred_at: string
  metadata?: Record<string, unknown>
}

interface UpcomingItem {
  id: string
  action_type: string
  name: string
  due_date?: string
  is_overdue: boolean
  plant_id?: string
  zone_id?: string
}

// ── API calls ─────────────────────────────────────────────────────────────────
function fetchActivity(dateStr: string): Promise<ActivityEvent[]> {
  const since = new Date(dateStr + 'T00:00:00')
  const until = new Date(dateStr + 'T23:59:59')
  return api
    .get('/activity', {
      params: {
        since: since.toISOString(),
        until: until.toISOString(),
        limit: 200,
      },
    })
    .then(r => r.data?.events ?? r.data ?? [])
    .catch(() => [])
}

function fetchUpcoming(): Promise<UpcomingItem[]> {
  return api.get('/care/upcoming').then(r => r.data?.upcoming ?? []).catch(() => [])
}

function fetchOverdue(): Promise<UpcomingItem[]> {
  return api.get('/care/overdue').then(r => r.data?.overdue ?? []).catch(() => [])
}

// ── Initials avatar ───────────────────────────────────────────────────────────
function Avatar({ label, size = 28 }: { label: string; size?: number }) {
  const initials = label
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  const colors = ['#F5A623', '#2DD4BF', '#60A5FA', '#A78BFA', '#F472B6', '#34D399']
  const color = colors[label.charCodeAt(0) % colors.length]
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color + '22', border: `1.5px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color, flexShrink: 0,
    }}>
      {initials || <User size={size * 0.5} />}
    </div>
  )
}

// ── Action badge ──────────────────────────────────────────────────────────────
function ActionBadge({ type, small }: { type: string; small?: boolean }) {
  const cfg = actionConfig(type)
  const Icon = cfg.icon
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '2px 6px' : '3px 8px',
      borderRadius: 6,
      background: cfg.color + '18',
      border: `1px solid ${cfg.color}33`,
      fontSize: small ? 11 : 12,
      fontWeight: 500,
      color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={small ? 10 : 11} />
      {cfg.label}
    </span>
  )
}

// ── Timeline event row ────────────────────────────────────────────────────────
function EventRow({ event }: { event: ActivityEvent }) {
  // activity actions look like "harvest.recorded" — extract the verb
  const [category] = event.action.split('.')
  const actionType = category === 'care' ? (event.metadata?.action_type as string ?? 'observation') : category

  return (
    <div style={{
      display: 'flex', gap: 12, padding: '12px 0',
      borderBottom: `1px solid ${C.border}`,
    }}>
      <Avatar label={event.actor_label} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {event.actor_label}
          </span>
          <ActionBadge type={actionType} small />
          {event.resource_label && (
            <span style={{ fontSize: 12, color: C.textFaint }}>
              · {event.resource_label}
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.45 }}>
          {event.description}
        </p>
      </div>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontSize: 12, color: C.textFaint }}>{formatTime(event.occurred_at)}</div>
        <div style={{ fontSize: 11, color: C.textFaint, marginTop: 1 }}>
          {formatRelative(event.occurred_at)}
        </div>
      </div>
    </div>
  )
}

// ── Upcoming / overdue item ────────────────────────────────────────────────────
function CareItem({ item }: { item: UpcomingItem }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 0', borderBottom: `1px solid ${C.border}`,
    }}>
      <ActionBadge type={item.action_type} small />
      <span style={{ flex: 1, fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.name}
      </span>
      {item.due_date && (
        <span style={{ fontSize: 11, color: item.is_overdue ? C.red : C.textFaint, flexShrink: 0 }}>
          {new Date(item.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  )
}

// ── Log Event modal ───────────────────────────────────────────────────────────
interface LogEventForm {
  action_type: string
  name: string
  notes: string
  occurred_at: string
  duration_minutes: string
  plant_label: string
}

function LogEventModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const now = new Date()
  const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 16)

  const [form, setForm] = useState<LogEventForm>({
    action_type:       'observation',
    name:              '',
    notes:             '',
    occurred_at:       localNow,
    duration_minutes:  '',
    plant_label:       '',
  })
  const [success, setSuccess] = useState(false)
  const [mode, setMode] = useState<'single' | 'narrative' | 'yield'>('single')

  // Yield-impact quick entry (universal yield-impacting event) — posts to
  // /yield-impact so it shows on the Yield Impact page + feeds yield reporting.
  const [yf, setYf] = useState({
    event_type: 'weather', severity: 'medium', title: '', description: '',
    occurred_at: localNow.slice(0, 10), impact_pct: '',
  })
  const yieldMut = useMutation({
    mutationFn: () => yieldImpactApi.create({
      event_type: yf.event_type, severity: yf.severity,
      title: yf.title || `${yf.event_type} event`,
      description: yf.description,
      occurred_at: yf.occurred_at,
      estimated_impact_pct: yf.impact_pct ? Number(yf.impact_pct) : undefined,
      source: 'manual',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['yield-impact'] })
      qc.invalidateQueries({ queryKey: ['activity'] })
      setSuccess(true)
      setTimeout(onClose, 1200)
    },
  })

  // Single Log Event writes to the activity feed (activity_events) via
  // /activity/commit — NOT the stubbed /care/events (which 201'd but never
  // persisted, so logged events never appeared in the feed).
  const mutation = useMutation({
    mutationFn: (ev: ParsedEvent) => activityApi.commit([ev]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activity'] })
      qc.invalidateQueries({ queryKey: ['care-upcoming'] })
      qc.invalidateQueries({ queryKey: ['care-overdue'] })
      setSuccess(true)
      setTimeout(onClose, 1200)
    },
    onError: (err) => {
      console.error('[activity/commit] mutation failed:', errStatus(err), apiError(err))
    },
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const cfg = actionConfig(form.action_type)
    const localStr = form.occurred_at.length === 16 ? form.occurred_at + ':00' : form.occurred_at
    const occurredAt = new Date(localStr).toISOString()
    const desc = [form.name || cfg.label, form.notes].filter(Boolean).join(' — ')
    mutation.mutate({
      action_type:   form.action_type,
      occurred_at:   occurredAt,
      description:   desc,
      resource_label: form.plant_label || undefined,
      confidence:    1,
    })
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    background: 'var(--color-bg)', border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '8px 12px',
    color: C.text, fontSize: 13,
    outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 500,
    color: C.textMuted, marginBottom: 5,
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: 'var(--color-bg-card)', border: `1px solid ${C.border}`,
        borderRadius: 14, width: mode === 'narrative' ? 580 : 440, maxWidth: '95vw',
        padding: 24, position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>
            Log Event
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Mode toggle: single form vs. free-text / spreadsheet → many events */}
        {!success && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: 'var(--color-bg)', padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
            {([['single', 'Single event'], ['narrative', 'Describe / paste'], ['yield', 'Yield impact']] as const).map(([m, label]) => (
              <button key={m} type="button" onClick={() => setMode(m)} style={{
                flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: mode === m ? 600 : 400,
                background: mode === m ? C.green : 'transparent',
                color: mode === m ? '#fff' : C.textMuted,
              }}>{label}</button>
            ))}
          </div>
        )}

        {mode === 'narrative' ? (
          <NarrativeEventCapture onCommitted={() => setTimeout(onClose, 1400)} />
        ) : success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#34D399' }}>
            <CheckCircle2 size={36} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Event logged!</div>
          </div>
        ) : mode === 'yield' ? (
          <form onSubmit={e => { e.preventDefault(); yieldMut.mutate() }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12, color: C.textFaint, lineHeight: 1.5 }}>
              Log something that helped or hurt yield (weather, pest, equipment failure, beneficial release…). It appears on the Yield Impact page and feeds reporting.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={yf.event_type} onChange={e => setYf(f => ({ ...f, event_type: e.target.value }))}>
                  {['weather', 'irrigation', 'equipment', 'pest', 'disease', 'beneficial', 'other'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Severity</label>
                <select style={inputStyle} value={yf.severity} onChange={e => setYf(f => ({ ...f, severity: e.target.value }))}>
                  {['low', 'medium', 'high', 'critical'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Title</label>
              <input style={inputStyle} placeholder="e.g. Hail storm over Kent block" value={yf.title} onChange={e => setYf(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>When</label>
                <input type="date" style={inputStyle} value={yf.occurred_at} onChange={e => setYf(f => ({ ...f, occurred_at: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Impact % <span style={{ color: C.textFaint }}>(±)</span></label>
                <input type="number" style={inputStyle} placeholder="-20" value={yf.impact_pct} onChange={e => setYf(f => ({ ...f, impact_pct: e.target.value }))} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} placeholder="What happened, what you did…" value={yf.description} onChange={e => setYf(f => ({ ...f, description: e.target.value }))} />
            </div>
            {yieldMut.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>{apiError(yieldMut.error, 'Failed to save')}</div>}
            <button type="submit" disabled={yieldMut.isPending} style={{ padding: '10px 0', borderRadius: 8, background: yieldMut.isPending ? C.green + '88' : C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: yieldMut.isPending ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {yieldMut.isPending ? 'Saving…' : 'Log yield-impact event'}
            </button>
          </form>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Action type */}
            <div>
              <label style={labelStyle}>Action type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ACTION_TYPES.map(a => {
                  const Icon = a.icon
                  const sel = form.action_type === a.value
                  return (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, action_type: a.value }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 8,
                        fontSize: 12, fontWeight: sel ? 600 : 400,
                        background: sel ? a.color + '22' : 'transparent',
                        border: `1px solid ${sel ? a.color + '55' : C.border}`,
                        color: sel ? a.color : C.textMuted,
                        cursor: 'pointer', transition: 'all 0.1s',
                      }}
                    >
                      <Icon size={11} /> {a.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description <span style={{ color: C.textFaint }}>(optional)</span></label>
              <input
                style={inputStyle}
                placeholder={`e.g. Applied potassium nitrate to grove section B`}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>

            {/* Plant / zone */}
            <div>
              <label style={labelStyle}>Plant or zone <span style={{ color: C.textFaint }}>(optional)</span></label>
              <input
                style={inputStyle}
                placeholder="e.g. Keitt block, Tree #42"
                value={form.plant_label}
                onChange={e => setForm(f => ({ ...f, plant_label: e.target.value }))}
              />
            </div>

            {/* Date / time + duration row */}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>When</label>
                <input
                  type="datetime-local"
                  style={inputStyle}
                  value={form.occurred_at}
                  onChange={e => setForm(f => ({ ...f, occurred_at: e.target.value }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Duration (min)</label>
                <input
                  type="number"
                  style={inputStyle}
                  placeholder="30"
                  min={1}
                  value={form.duration_minutes}
                  onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: 72, fontFamily: 'inherit' }}
                placeholder="Anything notable — product used, quantity, weather, observations..."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>

            {mutation.isError && (() => {
              const status = errStatus(mutation.error)
              const msg = apiError(mutation.error, 'Failed to save')
              const is404 = status === 404
              return (
                <div style={{ fontSize: 12, color: C.red, padding: '10px 12px', background: C.red + '11', borderRadius: 8, lineHeight: 1.5 }}>
                  <strong>{status ? `${status} — ` : ''}{msg}</strong>
                  {is404 && (
                    <div style={{ marginTop: 4, color: C.red + 'CC' }}>
                      The care events endpoint isn't available yet. Check that the API server has <code style={{ fontFamily: 'monospace' }}>POST /api/v1/care/events</code> implemented.
                    </div>
                  )}
                </div>
              )
            })()}

            <button
              type="submit"
              disabled={mutation.isPending}
              style={{
                padding: '10px 0', borderRadius: 8,
                background: mutation.isPending ? C.green + '88' : C.green,
                border: 'none', color: '#fff',
                fontSize: 14, fontWeight: 600, cursor: mutation.isPending ? 'not-allowed' : 'pointer',
                marginTop: 4,
              }}
            >
              {mutation.isPending ? 'Saving…' : 'Save event'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function ActivityPage() {
  const [date, setDate] = useState<Date>(new Date())
  const [showModal, setShowModal] = useState(false)
  const dateStr = toDateStr(date)
  const isToday = dateStr === toDateStr(new Date())

  function prevDay() { setDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n }) }
  function nextDay() { setDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n }) }

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['activity', dateStr],
    queryFn:  () => fetchActivity(dateStr),
    refetchInterval: isToday ? 30_000 : false,
  })

  const { data: upcoming = [] } = useQuery({
    queryKey: ['care-upcoming'],
    queryFn:  fetchUpcoming,
    staleTime: 5 * 60_000,
  })

  const { data: overdue = [] } = useQuery({
    queryKey: ['care-overdue'],
    queryFn:  fetchOverdue,
    staleTime: 5 * 60_000,
  })

  const upcomingToday   = upcoming.filter(i => i.due_date?.startsWith(dateStr))
  const upcomingFuture  = upcoming.filter(i => !i.due_date?.startsWith(dateStr))

  const dateLabel = isToday
    ? 'Today'
    : date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })

  const sectionHeader = (label: string, count?: number, danger?: boolean) => (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: danger ? C.red : C.textFaint,
      padding: '12px 0 6px',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {label}
      {count !== undefined && count > 0 && (
        <span style={{
          background: danger ? C.red + '22' : 'color-mix(in srgb, var(--color-primary) 13%, transparent)',
          color: danger ? C.red : C.amber,
          border: `1px solid ${danger ? C.red : C.amber}33`,
          borderRadius: 99, padding: '1px 6px', fontSize: 10, fontWeight: 700,
        }}>
          {count}
        </span>
      )}
    </div>
  )

  return (
    <>
      {showModal && <LogEventModal onClose={() => setShowModal(false)} />}

      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>
              Activity
            </h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>
              Farm event log — what happened, what's coming up
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: 9,
              background: C.green, border: 'none',
              color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Log Event
          </button>
        </div>

        {/* ── Two-column layout ────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

          {/* ── Left: timeline feed ──────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, overflow: 'hidden',
            }}>
              {/* Date nav */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px',
                borderBottom: `1px solid ${C.border}`,
              }}>
                <button
                  onClick={prevDay}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4, display: 'flex' }}
                >
                  <ChevronLeft size={18} />
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={15} style={{ color: C.amber }} />
                  <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                    {dateLabel}
                  </span>
                  {isToday && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)',
                      color: C.amber, border: `1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)`,
                      borderRadius: 99, padding: '1px 7px',
                    }}>LIVE</span>
                  )}
                </div>
                <button
                  onClick={nextDay}
                  disabled={isToday}
                  style={{ background: 'none', border: 'none', cursor: isToday ? 'not-allowed' : 'pointer', color: isToday ? C.textFaint : C.textMuted, padding: 4, display: 'flex' }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              {/* Events */}
              <div style={{ padding: '0 18px' }}>
                {eventsLoading ? (
                  <div style={{ padding: '40px 0', textAlign: 'center', color: C.textFaint, fontSize: 13 }}>
                    Loading…
                  </div>
                ) : events.length === 0 ? (
                  <div style={{ padding: '48px 0', textAlign: 'center' }}>
                    <Clock size={32} style={{ color: C.textFaint, marginBottom: 10, opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: 14, color: C.textFaint }}>
                      No events logged for {dateLabel.toLowerCase()}
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>
                      Tap "Log Event" to record what's happening in the field
                    </p>
                  </div>
                ) : (
                  events.map(e => <EventRow key={e.id} event={e} />)
                )}
              </div>
            </div>
          </div>

          {/* ── Right: overdue + upcoming ──────────────────────────────── */}
          <div style={{ width: 280, flexShrink: 0 }}>

            {/* Overdue */}
            {overdue.length > 0 && (
              <div style={{
                background: C.card, border: `1px solid ${C.red}33`,
                borderRadius: 12, padding: '0 16px 12px', marginBottom: 16,
              }}>
                {sectionHeader('Overdue', overdue.length, true)}
                {overdue.map(i => <CareItem key={i.id} item={i} />)}
              </div>
            )}

            {/* Due today */}
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: '0 16px 12px', marginBottom: 16,
            }}>
              {sectionHeader('Due today', upcomingToday.length)}
              {upcomingToday.length === 0 ? (
                <p style={{ fontSize: 12, color: C.textFaint, margin: '6px 0 4px' }}>
                  Nothing scheduled for today
                </p>
              ) : (
                upcomingToday.map(i => <CareItem key={i.id} item={i} />)
              )}
            </div>

            {/* Upcoming */}
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: '0 16px 12px',
            }}>
              {sectionHeader('Coming up', upcomingFuture.length)}
              {upcomingFuture.length === 0 ? (
                <p style={{ fontSize: 12, color: C.textFaint, margin: '6px 0 4px' }}>
                  No upcoming care scheduled
                </p>
              ) : (
                upcomingFuture.slice(0, 8).map(i => <CareItem key={i.id} item={i} />)
              )}
            </div>

            {/* Quick stats */}
            <div style={{
              marginTop: 16, padding: '14px 16px',
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12,
            }}>
              {sectionHeader('Today so far')}
              {[
                { label: 'Events logged', value: events.length },
                { label: 'Farm hands active', value: new Set(events.map(e => e.actor_label)).size },
                { label: 'Overdue items', value: overdue.length, warn: overdue.length > 0 },
              ].map(s => (
                <div key={s.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: 12, color: C.textMuted }}>{s.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: s.warn ? C.red : C.text }}>
                    {s.value}
                  </span>
                </div>
              ))}
            </div>

            {overdue.length > 0 && (
              <div style={{
                marginTop: 12, padding: '10px 14px',
                background: C.red + '11', border: `1px solid ${C.red}33`,
                borderRadius: 10, display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <AlertTriangle size={15} style={{ color: C.red, marginTop: 1, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12, color: C.red + 'CC', lineHeight: 1.5 }}>
                  {overdue.length} overdue care {overdue.length === 1 ? 'item' : 'items'}. Log them now or reschedule.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  )
}
